import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { trackPageView } from './utils/analytics'
import Header from './components/Common/Header'
import Dashboard from './components/Dashboard/Dashboard'
import ApprovalPage from './components/Approval/ApprovalPage'
import SchedulePage from './components/Schedule/SchedulePage'
import AnalyticsPage from './components/Analytics/AnalyticsPage'
import AccountsPage from './components/Accounts/AccountsPageV2'
import InstagramReconnectModal from './components/Accounts/InstagramReconnectModal'
import BrandSettingsPage from './components/BrandSettings/BrandSettingsPage'
import CreateStoriesPage from './components/Upload/CreateStoriesPage'
import LoginPage from './components/Auth/LoginPage'
import RegisterPage from './components/Auth/RegisterPage'
import ResetPasswordPage from './components/Auth/ResetPasswordPage'
import SettingsPage from './components/Settings/SettingsPage'
import BrevoChat from './components/Common/BrevoChat'
import { useAuth } from './hooks/useAuth'
import { AuthProvider } from './contexts/AuthContext'
import { AccountProvider } from './contexts/AccountContext'
import { WebSocketProvider } from './contexts/WebSocketContext'
import { useAccount } from './hooks/useAccount'
// Phase 3 Integration - Subscription Components
import QuotaExceededModal from './components/Subscription/QuotaExceededModal'
// E-Commerce Components
import EcommerceDashboard from './components/Ecommerce/EcommerceDashboard'
import { FeedManager } from './components/Ecommerce/FeedManager'
import { FeedImportWizard } from './components/Ecommerce/FeedImportWizard'
import { ProductManager } from './components/Ecommerce/ProductManager'
import { BioPageBuilder } from './components/Ecommerce/BioPageBuilder'
import { ProductTemplateSelector } from './components/Ecommerce/ProductTemplateSelector'
import { EcommerceStats, ConversionChart } from './components/Ecommerce/EcommerceAnalytics'
import { EcommerceCreateStoriesPage } from './components/Ecommerce/EcommerceCreateStoriesPage'
import FeatureLockedModal from './components/Subscription/FeatureLockedModal'
import SubscriptionPage from './components/Subscription/SubscriptionPage'
import SubscriptionSuccess from './components/Subscription/SubscriptionSuccess'
import SubscriptionCancelled from './components/Subscription/SubscriptionCancelled'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import './styles/customFonts.css'
// ApprovalModal v2 - Custom Toast System
import { ToastProvider } from './components/shared/Toast'
// Global Error Boundary
import ErrorBoundary from './components/ErrorBoundary'
// Cookie Consent
import CookieBanner from './components/CookieConsent/CookieBanner'
import Footer from './components/Layout/Footer'
// Legal Pages
import ImprintPage from './components/Legal/ImprintPage'
import PrivacyPage from './components/Legal/PrivacyPage'
import TermsPage from './components/Legal/TermsPage'
import ContactPage from './components/Legal/ContactPage'
import AVVPage from './components/Legal/AVVPage'

function ProtectedRoute({ children, requireAccount = false }) {
  const { user, loading } = useAuth()
  const { t } = useTranslation('common')
  const location = useLocation()
  const {
    isLoadingAccounts,
    selectedAccount,
    hasAccounts,
  } = useAccount()

  const isCheckingAuth = loading || (requireAccount && isLoadingAccounts)

  if (isCheckingAuth) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (requireAccount && !selectedAccount && !hasAccounts) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md text-center space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">{t('empty.noAccounts')}</h2>
          <p className="text-gray-600">
            {t('empty.noAccountsMessage')}
          </p>
        </div>
      </div>
    )
  }

  return children
}

function AppContent() {
  const { user, loading } = useAuth()
  const { isLoadingAccounts } = useAccount()
  const location = useLocation()

  // Track page views on route changes
  useEffect(() => {
    trackPageView()
  }, [location])

  if (loading || isLoadingAccounts) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />}
        />
        <Route
          path="/register"
          element={user ? <Navigate to="/dashboard" replace /> : <RegisterPage />}
        />
        <Route
          path="/reset-password"
          element={<ResetPasswordPage />}
        />

        {/* Legal Pages (Public) */}
        <Route path="/impressum" element={<ImprintPage />} />
        <Route path="/imprint" element={<ImprintPage />} />
        <Route path="/datenschutz" element={<PrivacyPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/avv" element={<AVVPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Protected Routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-gray-50 flex flex-col">
                <Header />
                <main className="container mx-auto px-4 py-8 flex-1">
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/create" element={<CreateStoriesPage />} />
                    <Route path="/approval" element={<ApprovalPage />} />
                    <Route path="/schedule" element={<SchedulePage />} />
                    <Route path="/analytics" element={<AnalyticsPage />} />
                    <Route path="/accounts" element={<AccountsPage />} />
                    <Route path="/accounts/:accountId/brand-settings" element={<BrandSettingsPage />} />
                    <Route path="/brand-settings" element={<BrandSettingsPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    {/* Phase 3 Integration - Subscription Pages */}
                    <Route path="/subscription" element={<SubscriptionPage />} />
                    <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                    <Route path="/subscription/cancelled" element={<SubscriptionCancelled />} />
                    {/* E-Commerce Routes */}
                    <Route path="/ecommerce" element={<EcommerceDashboard />} />
                    <Route path="/ecommerce/feeds" element={<FeedManager />} />
                    <Route path="/ecommerce/feeds/new" element={<FeedImportWizard />} />
                    <Route path="/ecommerce/products" element={<ProductManager />} />
                    <Route path="/ecommerce/bio-pages/new" element={<BioPageBuilder />} />
                    <Route path="/ecommerce/bio-pages/:bioPageId" element={<BioPageBuilder />} />
                    <Route path="/ecommerce/templates" element={<ProductTemplateSelector />} />
                    <Route path="/ecommerce/create-stories" element={<EcommerceCreateStoriesPage />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </main>
                <Footer />
              </div>
            </ProtectedRoute>
          }
        />
      </Routes>

      {/* Phase 3 Integration - Global Modals and Toast Notifications */}
      <QuotaExceededModal />
      <InstagramReconnectModal />
      <FeatureLockedModal />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Cookie Consent Banner */}
      <CookieBanner />
    </>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AccountProvider persistenceMode="url">
          <WebSocketProvider>
            <ToastProvider>
              <AppContent />
              <BrevoChat />
            </ToastProvider>
          </WebSocketProvider>
        </AccountProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
